import type { ComponentDetailsData, ComponentDetailsSection } from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { ExpandableCards } from "@stryker/core/types/jobs";
import { type ChangeEvent, Fragment, useEffect, useMemo, useState } from "react";
import { clone, isEmpty } from "remeda";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import { filterMaterialGroupsByDesignCode } from "@/utils/materialFilters";

import ExpandableSection from "../../../../components/job/ExpandableSection";

const checkOptions = [
  { value: "bend", label: "Bends" },
  { value: "tee_value_nozzles_or_90_with_dummy_leg", label: "Tee, Nozzles, or 90 with dummy leg" },
  { value: "flange", label: "Flange or Valve" },
  { value: "reducer", label: "Reducer" },
  { value: "end_cap_or_vessel_dome", label: "End Cap or Vessel Dome" },
  { value: "vessel_supports_saddles_or_attachments", label: "Vessel Supports, Saddles, or Attachments" },
  { value: "none", label: "None of the Above" },
];

const cardName = "componentDetails";

type FieldErrors = {
  component_nps?: string;
  component_schedule?: string;
  component_diameter?: string;
  unit_for_component_diameter?: string;
  component_thickness?: string;
  unit_for_component_thickness?: string;
};

export type SingleComponentData = {
  component_nps: null | string | "custom";
  component_diameter: number | null;
  unit_for_component_diameter: null | string;
  component_thickness: number | null;
  unit_for_component_thickness: null | string;
  component_schedule: null | number;
  is_flanges: boolean;
};
const emptyInitialData: SingleComponentData = {
  component_nps: null,
  component_diameter: null,
  unit_for_component_diameter: "Inches",
  component_thickness: null,
  unit_for_component_thickness: "Inches",
  component_schedule: null,
  is_flanges: false,
};

type ComponentDetailsProps = {
  savedComponentDetails: ComponentDetailsSection;
  toggleExpandableSection: (section: ExpandableCards) => void;
  setComponentDetails: (payload: { data: ComponentDetailsData; status: ExpandStatus }) => void;
  designCode?: string | null;
};
export function ComponentDetails({
  savedComponentDetails,
  toggleExpandableSection,
  setComponentDetails,
  designCode: designCodeProp,
}: ComponentDetailsProps) {
  const [component_options, setComponentOptions] = useState<string[]>([]);
  const [component_material, setComponent_material] = useState<null | number>(null);
  const [components_data, set_components_data] = useState<SingleComponentData[]>([emptyInitialData]);
  const [materialError, setMaterialError] = useState<string | null>(null);
  const [componentOptionsError, setComponentOptionsError] = useState<string | null>(null);
  const [componentErrors, setComponentErrors] = useState<FieldErrors[]>([{}]);

  // const { sections, toggleExpandableSection, setComponentDetails } = useCreateUpdateJobStore();
  // const savedComponentDetails = sections.componentDetails;

  const { npsOptions, getScheduleOptions, componentMaterialGroups, measureUnitOptions } = useConfigOptions();
  const storedDesignCode = useCUCompositeJobStore((state) => state.sections.jobDetails.data.design_code);
  const designCode = designCodeProp ?? storedDesignCode;
  const filteredMaterialGroups = useMemo(
    () => filterMaterialGroupsByDesignCode(componentMaterialGroups, designCode) ?? [],
    [componentMaterialGroups, designCode]
  );

  useEffect(() => {
    if (savedComponentDetails.isDataFilled && isEmpty(component_options)) {
      setComponentOptions(savedComponentDetails.data.component_options);
      setComponent_material(savedComponentDetails.data.component_material);
      set_components_data(savedComponentDetails.data.components_data);
    }
  }, [savedComponentDetails.isDataFilled]);

  const validateForm = () => {
    // Clear previous errors
    setMaterialError(null);
    setComponentOptionsError(null);
    setComponentErrors(components_data.map(() => ({})));

    let isValid = true;
    const newComponentErrors: FieldErrors[] = [];

    // Validate component options
    if (isEmpty(component_options)) {
      setComponentOptionsError("Please select at least one component option");
      isValid = false;
    }

    // Validate component material
    if (!component_material) {
      setMaterialError("Component material is required");
      isValid = false;
    }

    // Validate each component
    components_data.forEach((component, index) => {
      const errors: FieldErrors = {};

      if (!component.component_nps) {
        errors.component_nps = "Component NPS is required";
        isValid = false;
      }

      if (component.component_nps !== "custom") {
        if (!component.component_schedule) {
          errors.component_schedule = "Component schedule is required";
          isValid = false;
        }
      }

      if (component.component_nps === "custom") {
        if (!component.is_flanges) {
          if (!component.component_diameter) {
            errors.component_diameter = "Component diameter is required";
            isValid = false;
          }
          if (!component.component_thickness) {
            errors.component_thickness = "Component thickness is required";
            isValid = false;
          }
          if (!component.unit_for_component_diameter) {
            errors.unit_for_component_diameter = "Unit for diameter is required";
            isValid = false;
          }
          if (!component.unit_for_component_thickness) {
            errors.unit_for_component_thickness = "Unit for thickness is required";
            isValid = false;
          }
        } else {
          if (!component.component_diameter) {
            errors.component_diameter = "Component diameter is required";
            isValid = false;
          }
          if (!component.unit_for_component_diameter) {
            errors.unit_for_component_diameter = "Unit for diameter is required";
            isValid = false;
          }
        }
      }

      newComponentErrors.push(errors);
    });

    setComponentErrors(newComponentErrors);
    return isValid;
  };

  const validateSingleComponent = (component: SingleComponentData): FieldErrors => {
    const errors: FieldErrors = {};

    if (!component.component_nps) {
      errors.component_nps = "Component NPS is required";
    }

    if (component.component_nps === "custom") {
      if (!component.is_flanges) {
        if (!component.component_diameter) {
          errors.component_diameter = "Component diameter is required";
        }
        if (!component.component_thickness) {
          errors.component_thickness = "Component thickness is required";
        }
        if (!component.unit_for_component_diameter) {
          errors.unit_for_component_diameter = "Unit for diameter is required";
        }
        if (!component.unit_for_component_thickness) {
          errors.unit_for_component_thickness = "Unit for thickness is required";
        }
      } else {
        if (!component.component_diameter) {
          errors.component_diameter = "Component diameter is required";
        }
        if (!component.unit_for_component_diameter) {
          errors.unit_for_component_diameter = "Unit for diameter is required";
        }
      }
    } else {
      // For non-custom NPS, schedule is required
      if (!component.component_schedule) {
        errors.component_schedule = "Component schedule is required";
      }
    }

    return errors;
  };

  const handleSubmitForm = () => {
    if (validateForm()) {
      const mappedData = components_data.map((d, idx) => {
        // For non-custom NPS, remove custom diameter/thickness fields by omitting them
        if (d.component_nps !== "custom") {
          const {
            component_diameter,
            unit_for_component_diameter,
            component_thickness,
            unit_for_component_thickness,
            ...rest
          } = d;
          // Return the rest without the custom fields - they will be omitted from the API payload
          return rest as SingleComponentData;
        }
        // For custom NPS with flanges, remove thickness fields
        if (d.component_nps === "custom" && d.is_flanges) {
          const { component_thickness, unit_for_component_thickness, ...rest } = d;
          return rest as SingleComponentData;
        }
        // For custom NPS without flanges, keep all fields
        return d;
      });
      setComponentDetails({
        status: "done",
        data: {
          component_options,
          components_data: mappedData,
          component_material,
        },
      });
    }
  };

  !npsOptions.some((d) => d.title === "custom") && npsOptions.unshift({ title: "custom", value: "custom" });

  const inputWidth = "w-[270px]";
  return (
    <ExpandableSection
      status={savedComponentDetails.status}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Component Details"
    >
      <Typography>Please check any that can be applied</Typography>
      <div
        className={cn(
          "my-4 flex flex-col gap-2  pb-4",
          !isEmpty(component_options) && "border-b border-solid border-b-gray-200",
          componentOptionsError && "border-error-300"
        )}
      >
        {checkOptions.map((o) => {
          return (
            <Checkbox
              key={o.value}
              checked={component_options.includes(o.value)}
              onChange={(value) => {
                setComponentOptions((prevState) => {
                  let next: string[];
                  if (value && o.value === "none") {
                    next = ["none"];
                  } else {
                    next = value
                      ? [...prevState, o.value].filter((o) => o !== "none")
                      : prevState.filter((v) => v !== o.value);
                  }
                  if (next.length > 0 && componentOptionsError) {
                    setComponentOptionsError(null);
                  }
                  return next;
                });
              }}
              label={o.label}
            />
          );
        })}
        {componentOptionsError ? (
          <Typography className="mt-1 text-error-500" variant="text-small">
            {componentOptionsError}
          </Typography>
        ) : null}
      </div>

      {!isEmpty(component_options) && (
        <div className="flex flex-col">
          <Typography variant="text-small" weight="medium" className="mb-1 text-gray-700">
            Component Material
          </Typography>
          <Select
            value={component_material !== null ? String(component_material) : undefined}
            onValueChange={(option) => {
              setComponent_material(Number(option));
              if (option && materialError) {
                setMaterialError(null);
              }
            }}
          >
            <SelectTrigger
              className={cn(
                inputWidth,
                !component_material && "[&>span]:text-gray-400",
                materialError && "border-error-300"
              )}
            >
              <SelectValue placeholder="Component Material" className="text-red-950" />
            </SelectTrigger>
            <SelectContent>
              {filteredMaterialGroups.map((group, groupIndex) => {
                return (
                  <Fragment key={group.group}>
                    {groupIndex > 0 && <SelectSeparator />}
                    <SelectGroup>
                      <SelectLabel>{group.group}</SelectLabel>
                      {group.options.map((option) => {
                        const descriptor = option.type ?? option.general_material;
                        const label = descriptor ? `${option.material} - ${descriptor}` : option.material;
                        return (
                          <SelectItem key={option.id} value={String(option.id)}>
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  </Fragment>
                );
              })}
            </SelectContent>
          </Select>
          {materialError ? (
            <Typography className="mt-1 text-error-500" variant="text-small">
              {materialError}
            </Typography>
          ) : null}
          {components_data.map((component, index) => {
            const scheduleOptions: any[] =
              component.component_nps !== "custom" ? getScheduleOptions(component.component_nps) : [];
            const componentError = componentErrors[index] || {};
            const handleChangeData = (value: unknown, place: string) => {
              set_components_data((prevState) => {
                const next = prevState.map((p, i) => {
                  if (i === index) {
                    const updated = {
                      ...p,
                      [place]: value,
                    } as SingleComponentData;
                    return updated;
                  }
                  return p;
                });
                // Recompute and update errors for this component so messages hide when valid
                setComponentErrors((prev) => {
                  const nextErrors = [...prev];
                  nextErrors[index] = validateSingleComponent(next[index]);
                  return nextErrors;
                });
                return next;
              });
            };

            return (
              <div
                key={index}
                className={cn(
                  "flex flex-wrap gap-4 pb-2",
                  components_data.length - 1 !== index && "border-b border-solid border-b-gray-200"
                )}
              >
                <div className="my-2 flex min-w-full items-end gap-4">
                  <Typography variant="display-xs" weight="semibold">
                    Component {index + 1}
                  </Typography>
                  <Checkbox
                    checked={component.is_flanges}
                    onChange={(value) => {
                      handleChangeData(value, "is_flanges");
                      if (value) {
                        handleChangeData("custom", "component_nps");
                        // Set default units to "Inches" for flanges if not already set
                        if (!component.unit_for_component_diameter) {
                          handleChangeData("Inches", "unit_for_component_diameter");
                        }
                      }
                    }}
                    label="Flanges"
                  />

                  {index > 0 && (
                    <Button
                      onClick={() => {
                        set_components_data((prevState) => {
                          const copy = clone(prevState);
                          copy.splice(index, 1);
                          return copy;
                        });
                        setComponentErrors((prevState) => {
                          const copy = clone(prevState);
                          copy.splice(index, 1);
                          return copy;
                        });
                      }}
                    >
                      Remove Component
                    </Button>
                  )}
                </div>
                <div className="flex flex-col">
                  <Typography
                    variant="text-small"
                    weight="medium"
                    className="mb-1 text-gray-700"
                  >{`NPS ${index + 1}`}</Typography>
                  <Select
                    value={component.component_nps ?? undefined}
                    onValueChange={(option) => {
                      handleChangeData(option, "component_nps");
                      if (option === "custom") {
                        // Clear schedule if switching to custom NPS
                        handleChangeData(null, "component_schedule");
                        // Set default units to "Inches" if not already set
                        if (!component.unit_for_component_diameter) {
                          handleChangeData("Inches", "unit_for_component_diameter");
                        }
                        if (!component.unit_for_component_thickness) {
                          handleChangeData("Inches", "unit_for_component_thickness");
                        }
                      } else {
                        // Clear custom diameter/thickness fields when switching away from custom
                        handleChangeData(null, "component_diameter");
                        handleChangeData(null, "unit_for_component_diameter");
                        handleChangeData(null, "component_thickness");
                        handleChangeData(null, "unit_for_component_thickness");
                      }
                    }}
                  >
                    <SelectTrigger
                      className={cn(
                        inputWidth,
                        !component.component_nps && "[&>span]:text-gray-400",
                        componentError.component_nps && "border-error-300"
                      )}
                    >
                      <SelectValue placeholder={`NPS ${index + 1} `} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>{`NPS ${index + 1} `}</SelectLabel>
                        {npsOptions.map((option) => {
                          return <SelectItem value={option.value}>{option.title}</SelectItem>;
                        })}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {componentError.component_nps ? (
                    <Typography className="mt-1 text-error-500" variant="text-small">
                      {componentError.component_nps}
                    </Typography>
                  ) : null}
                </div>

                {component.component_nps === "custom" ? (
                  <>
                    <TextInput
                      className={inputWidth}
                      label={`Custom Diameter ${index + 1}`}
                      type="number"
                      name="component_diameter"
                      step={inputStep}
                      required={true}
                      max={maxInputNumber}
                      value={component.component_diameter ?? ""}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        handleChangeData(event.target.value, "component_diameter");
                      }}
                      error={componentError.component_diameter}
                    />
                    <div className="flex flex-col">
                      <Typography
                        variant="text-small"
                        weight="medium"
                        className="mb-1 text-gray-700"
                      >{`Unit for Custom Diameter ${index + 1}`}</Typography>
                      <Select
                        value={component.unit_for_component_diameter ?? undefined}
                        onValueChange={(option) => {
                          handleChangeData(option, "unit_for_component_diameter");
                        }}
                      >
                        <SelectTrigger
                          className={cn(
                            inputWidth,
                            !component.unit_for_component_diameter && "[&>span]:text-gray-400",
                            componentError.unit_for_component_diameter && "border-error-300"
                          )}
                        >
                          <SelectValue placeholder={`Unit for Custom Diameter ${index + 1}`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>{`Unit for Custom Diameter ${index + 1}`}</SelectLabel>
                            {measureUnitOptions.map((option) => {
                              return (
                                <SelectItem key={`diameter-unit-${index}-${option.value}`} value={option.value}>
                                  {option.title}
                                </SelectItem>
                              );
                            })}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      {componentError.unit_for_component_diameter ? (
                        <Typography className="mt-1 text-error-500" variant="text-small">
                          {componentError.unit_for_component_diameter}
                        </Typography>
                      ) : null}
                    </div>
                    {!component.is_flanges && (
                      <>
                        <TextInput
                          className={inputWidth}
                          label={`Custom Thickness ${index + 1}`}
                          type="number"
                          name="component_thickness"
                          step={inputStep}
                          required={true}
                          max={maxInputNumber}
                          value={component.component_thickness ?? ""}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            handleChangeData(event.target.value, "component_thickness");
                          }}
                          error={componentError.component_thickness}
                        />
                        <div className="flex flex-col">
                          <Typography
                            variant="text-small"
                            weight="medium"
                            className="mb-1 text-gray-700"
                          >{`Unit for Custom Thickness ${index + 1}`}</Typography>
                          <Select
                            value={component.unit_for_component_thickness ?? undefined}
                            onValueChange={(option) => {
                              handleChangeData(option, "unit_for_component_thickness");
                            }}
                          >
                            <SelectTrigger
                              className={cn(
                                inputWidth,
                                !component.unit_for_component_thickness && "[&>span]:text-gray-400",
                                componentError.unit_for_component_thickness && "border-error-300"
                              )}
                            >
                              <SelectValue placeholder={`Unit for Custom Thickness ${index + 1}`} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectLabel>{`Unit for Custom Thickness ${index + 1}`}</SelectLabel>
                                {measureUnitOptions.map((option) => {
                                  return (
                                    <SelectItem key={`thickness-unit-${index}-${option.value}`} value={option.value}>
                                      {option.title}
                                    </SelectItem>
                                  );
                                })}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                          {componentError.unit_for_component_thickness ? (
                            <Typography className="mt-1 text-error-500" variant="text-small">
                              {componentError.unit_for_component_thickness}
                            </Typography>
                          ) : null}
                        </div>
                      </>
                    )}
                  </>
                ) : null}
                {component.component_nps !== "custom" ? (
                  <div className="flex flex-col">
                    <Typography
                      variant="text-small"
                      weight="medium"
                      className="mb-1 text-gray-700"
                    >{`Schedule ${index + 1}`}</Typography>
                    <Select
                      value={component.component_schedule ? String(component.component_schedule) : undefined}
                      onValueChange={(option) => {
                        handleChangeData(option, "component_schedule");
                      }}
                    >
                      <SelectTrigger
                        className={cn(
                          inputWidth,
                          !component.component_schedule && "[&>span]:text-gray-400",
                          componentError.component_schedule && "border-error-300"
                        )}
                      >
                        <SelectValue placeholder={`Schedule ${index + 1}`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>{`Schedule ${index + 1}`}</SelectLabel>
                          {scheduleOptions.map((option) => {
                            return <SelectItem value={option.value}>{option.title}</SelectItem>;
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {componentError.component_schedule ? (
                      <Typography className="mt-1 text-error-500" variant="text-small">
                        {componentError.component_schedule}
                      </Typography>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}

          <Button
            className="my-2 w-fit"
            onClick={() => set_components_data((prevState) => [...prevState, emptyInitialData])}
          >
            + Component
          </Button>
          <Button onClick={handleSubmitForm} className="mt-4 self-end">
            Save
          </Button>
        </div>
      )}
    </ExpandableSection>
  );
}
